import React, {FC, memo, useEffect, useRef, useState} from "react";
import {Alert, Button, Modal, Space, message} from "antd";
import {useHistory} from "react-router-dom";
import {ApolloProvider, useApolloClient} from "@apollo/client";
import {
    ArrayDataOptions,
    BookingDeleteActionType,
    BookingType,
    ExecuteTransferMutation,
    GetBookedAttendeesByCourseIdDocument,
    GetBookingsByCourseDocument,
    GetBookingsByCourseMinimalDocument,
    GetCourseByIdDocument,
    TablesEnum,
    TransferCommunicationInput,
    TransferOldBookingOption,
    TransferPlanInput,
    TransferPriceKind,
    TransferSurplusHandling,
    useExecuteTransferMutation,
    useGetBookingByIdQuery,
    useGetCoursesWithPaginationQuery,
    useTransferPlanPreviewLazyQuery,
    useVoidMolliePaymentLinkForBookingMutation,
} from "../../../generated/graphql";
import {URL_BOOKING_EDIT_ID, URL_COURSES_DETAILS_ID, URL_INSTRUCTOR_BOOKING_EDIT_ID, URL_INSTRUCTOR_INVOICE_SETTLEMENT_DETAILS, URL_INVOICE_SETTLEMENT_DETAILS, URL_LESSON_BOOKING_EDIT_ID, URL_LESSON_BOOKINGS_SETTLED_DETAILS} from "../../../models/url";
import CourseList from "../../courses/CourseEdit/CourseList";
import {TableType} from "../../courses/CourseEdit/CourseListColumns";
import {useLocalStorageOptions} from "../../../helpers/columnsLocalStorageHandler";
import {useIsStaffOnlyAccess} from "../../../helpers/useIsStaffOnlyAccess";
import {marginS} from "../../../styles/layout";
import i18n from "../../../services/i18n";
import useTransferWizardMode from "../../../hooks/useTransferWizardMode";
import {CREDIT_KEEP, CREDIT_REAPPLY, defaultOldBookingOption, itemDecisionsValid, manualPriceValid, OLD_BOOKING_AUTO, OLD_BOOKING_CUTOFF, OutcomeUnknownAlert, outcomeUnknownDetail, Plan, proRataSuggested, resolveCommunication, SURPLUS_CREDIT, surplusChoiceValid} from "./TransferWizardSteps";
import TransferSummary, {CourseInfo, CreditPlans, Decisions} from "./TransferSummary";
import TransferResult from "./TransferResult";
import formatDate from "../../../helpers/formatDate";
import {TRANSFER_WIZARD_WIDTH_COMPACT, TRANSFER_WIZARD_WIDTH_FULL, transferWizardModalStyle} from "./transferWizardLayout";
import ActiveDunningAlert from "../../invoice/SettlementDetails/BookingDunnings/ActiveDunningAlert";

/**
 * Umbuchungs-Wizard (#662, ADR 0006) — der Dialog des Wizard-Modus (`transferMode =
 * Wizard` UND Superuser-Flag `featureTransferWizard`).
 *
 * Zwei Schritte: Zielkurs → Zusammenfassung. Die Zusammenfassung zeigt sofort, was mit der
 * bisherigen und der neuen Buchung passiert, und fragt nur die Punkte ab, die wirklich
 * eine Entscheidung brauchen (Rabatt-Frage, Überschuss, Zusatzposten, besuchte Lektionen).
 * Jede Änderung einer Entscheidung lädt die Vorschau neu; geschrieben wird ausschließlich
 * über „Umbuchung jetzt ausführen" mit dem Token der zuletzt geladenen Vorschau. Die
 * Vorschau ist die Rechengrundlage der `expected*`-Preconditions: rechnet der Server unter
 * der Zeilensperre etwas anderes, bricht er ab.
 *
 * Preisoptionen anteilig/manuell und „besuchte Lektionen berechnen" (#663), Mail und
 * Anhänge (#664) sowie die Verwendung des Überschusses (#665: Guthaben, Mollie-Refund,
 * manuell erstattet) sind eingehängt. Bewusst nicht: Nachrücken im Quellkurs
 * (`DO_NOTHING`, #859).
 */

export type TransferWizardProps = {
    bookingId: string;
    currentCourseId: string;
    modalOpen: boolean;
    setModalOpen: Function;
    attendeeFullName?: string;
    /** Meldet die Kursauswahl nach außen (der alte Dialog hängt seinen Platzreservierungs-Knopf daran). */
    onCourseSelected?: (course: TableType | undefined) => void;
    /** Legacy-Platzreservierungs-Knopf des alten Dialogs (Admin+), unverändert übernommen. */
    placeReservationButton?: (selectedCourse: TableType | undefined, disabled: boolean) => React.ReactNode;
};

type ExecuteResult = ExecuteTransferMutation["executeTransfer"];

const STEP_TARGET = 0;
const STEP_SUMMARY = 1;

const INITIAL_DECISIONS: Decisions = {priceOption: undefined, manualPrice: {reason: ""}, oldBookingOption: OLD_BOOKING_AUTO, itemDecisions: [], surplusChoice: {kind: SURPLUS_CREDIT, reason: ""}, creditChoice: CREDIT_REAPPLY};

/** Erst mit gültigen Entscheidungen wird eine Vorschau geladen bzw. ausgeführt (Serverregeln: Grund bei manuellem Preis, Verzicht, manueller Erstattung). */
export const decisionsValid = (decisions: Decisions): boolean =>
    Boolean(decisions.priceOption)
    && (decisions.priceOption !== TransferPriceKind.Manual || manualPriceValid(decisions.manualPrice))
    && itemDecisionsValid(decisions.itemDecisions)
    && surplusChoiceValid(decisions.surplusChoice);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractGraphQlErrorCode = (error: any): string | undefined => (error?.graphQLErrors?.[0]?.extensions ? error.graphQLErrors[0].extensions.code : undefined);

const TransferWizard: FC<TransferWizardProps> = ({bookingId, currentCourseId, modalOpen, setModalOpen, attendeeFullName, onCourseSelected, placeReservationButton}) => {
    const tr = i18n.containers.bookings.TransferWizard;
    const history = useHistory();
    // `Modal.confirm` rendert seinen Inhalt in einem eigenen React-Baum ohne Provider — der
    // Ergebnis-Dialog braucht Apollo aber für die PDF-Vorschau der Belege.
    const apolloClient = useApolloClient();
    const isStaffOnlyAccess = useIsStaffOnlyAccess();
    const {currency, defaults} = useTransferWizardMode();

    // ── Schritt-Zustand ───────────────────────────────────────────────────────────────
    const [step, setStep] = useState(STEP_TARGET);
    const [selectedCourse, setSelectedCourse] = useState<TableType | undefined>(undefined);
    /** Zielkurs nach dem Aktualisieren beim Verlassen von Schritt 1 (frische freie Plätze). */
    const [targetCourse, setTargetCourse] = useState<TableType | undefined>(undefined);
    const [decisions, setDecisions] = useState<Decisions>(INITIAL_DECISIONS);
    /** Nutzerwahl zur Kommunikation; alles Ungesetzte fällt auf `communicationDefaults` der Vorschau zurück. */
    const [communicationOverrides, setCommunicationOverrides] = useState<TransferCommunicationInput>({});
    const [deleteNotes, setDeleteNotes] = useState(false);
    /** Vorschau mit Kurspreis (Grundlage für Rabatt-Frage, Posten, frühe Blocker). */
    const [basePlan, setBasePlan] = useState<Plan | undefined>(undefined);
    /** Vorschau mit KeepSourceDiscount — nur geladen, wenn A einen Rabatt trägt. */
    const [discountPlan, setDiscountPlan] = useState<Plan | undefined>(undefined);
    /**
     * Beide Guthaben-Varianten ZUM AKTUELLEN Plan-Input (nur `creditedBalanceHandling` verschieden),
     * damit die Guthaben-Karten die Folgen der gerade gewählten Preis-/Posten-Entscheidungen nennen.
     * Nur geladen, wenn A verrechnetes Guthaben trägt und B eine Buchung ist.
     */
    const [creditPlans, setCreditPlans] = useState<CreditPlans | undefined>(undefined);
    /** Vorschau mit ALLEN Entscheidungen; Token dieser Vorschau wird ausgeführt. */
    const [plan, setPlan] = useState<Plan | undefined>(undefined);
    /** Plan-Input (serialisiert), zu dem `plan` gehört — weicht er vom aktuellen ab, ist die Vorschau veraltet. */
    const [loadedKey, setLoadedKey] = useState<string | undefined>(undefined);
    // Zuletzt angeforderter Plan-Input: verhindert, dass eine gescheiterte Vorschau in einer Schleife neu geladen wird.
    const attemptedKeyRef = useRef<string | undefined>(undefined);
    const [errorText, setErrorText] = useState<string | undefined>(undefined);
    /** Technische Meldung, wenn die Ausführung ohne Antwort endete (`outcomeUnknownDetail`) — sperrt jede weitere Ausführung. */
    const [outcomeUnknown, setOutcomeUnknown] = useState<string | undefined>(undefined);
    const [busy, setBusy] = useState(false);

    // Doppel-Submit-Riegel: `busy` wird erst beim nächsten Render sichtbar, der Ref sofort.
    // Ohne ihn ergäben zwei Klicks zwei `executeTransfer`-Aufrufe mit je eigenem Token.
    const busyRef = useRef(false);
    // Dialog kann während eines Roundtrips geschlossen werden: danach nichts mehr setzen.
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // ── Kursliste (wie im bisherigen Dialog) ─────────────────────────────────────────
    const [totalResults, setTotalResults] = useState<number>(0);
    const [options, setOptions] = useLocalStorageOptions({localStorageName: "transferAttendeeToCourse", defaultOptions: {}, totalResults});
    const {data: coursesData, loading: loadingCourses, refetch: refetchCourses} = useGetCoursesWithPaginationQuery({variables: {options: options as ArrayDataOptions}});

    useEffect(() => {
        totalResults !== coursesData?.coursesWithPagination?.total && setTotalResults(coursesData?.coursesWithPagination?.total ?? 0);
    }, [coursesData, totalResults]);

    const courses = coursesData?.coursesWithPagination?.items ?? [];
    const tableData = {
        dataSource: courses.filter((course) => course.id !== currentCourseId) as Array<TableType>,
        loading: loadingCourses,
        total: coursesData?.coursesWithPagination?.total ?? 0,
    };

    const {data: bookingByIdData} = useGetBookingByIdQuery({variables: {id: bookingId}, skip: !modalOpen || !bookingId, fetchPolicy: "cache-and-network"});
    const sourceBooking = bookingByIdData?.bookingById;

    // ── Engine ────────────────────────────────────────────────────────────────────────
    // `network-only`: die Vorschau ist die Rechengrundlage der Preconditions, ein Cache-
    // Treffer könnte einen veralteten Geldstand bestätigen.
    const [loadTransferPlanPreview] = useTransferPlanPreviewLazyQuery({fetchPolicy: "network-only"});

    // Refetch-Regel wie im Sammel-Wizard (Prüfung 24.09.2026): Namen laden nur gemountete
    // Abfragen neu (kostenlos, decken Navigation während der Ausführung), Dokument-Einträge nur
    // für Cache-Einträge, die später cache-first gelesen werden (Kurs, gebuchte Teilnehmer von A
    // und B). Entfernt, weil nie gelesen: Abrechnungslisten, GetBookins (Name ebenfalls — keine
    // Komponente führt die Abfrage aus), Buchungsübersicht, Kursdetails von A und B (die Kursseite
    // lädt no-cache; ihr Name bleibt), Buchungen des Teilnehmers mit festen Variablen (→ Name).
    const transferRefetchQueries = [
        "GetBookingsByCourseMinimal",
        {query: GetBookingsByCourseMinimalDocument, variables: {id: targetCourse?.id}},
        {query: GetBookingsByCourseMinimalDocument, variables: {id: currentCourseId}},
        "GetBookingsByCourse",
        {query: GetBookingsByCourseDocument, variables: {id: targetCourse?.id}},
        {query: GetBookingsByCourseDocument, variables: {id: currentCourseId}},
        "getCourseById",
        {query: GetCourseByIdDocument, variables: {id: currentCourseId}},
        {query: GetCourseByIdDocument, variables: {id: targetCourse?.id}},
        "GetBookedAttendeesByCourseId",
        {query: GetBookedAttendeesByCourseIdDocument, variables: {courseId: currentCourseId}},
        {query: GetBookedAttendeesByCourseIdDocument, variables: {courseId: targetCourse?.id}},
        "GetCourseDetailsById",
        "GetCoursesWithPagination",
        "getFreePlacesByCourseId",
        "GetSettledBookings",
        "GetUnsettledBookings",
        "GetBookingsOverviewOptimized",
        "GetBookingsByAttendeeIdWithOptions",
        "GetBookerDetails",
        "GetBookersOverview",
        "GetDynamicBookers",
        "GetCreditEntriesByBookerId",
    ];
    const [executeTransfer] = useExecuteTransferMutation({refetchQueries: transferRefetchQueries});
    // Ohne Refetch: die Kursseite ersetzt ihre Buchungsliste beim Laden durch einen Spinner und baut
    // damit auch diesen Dialog ab (Browser-Befund 24.09.2026). Der Wizard lädt seine Vorschau selbst neu,
    // die Listen erst beim Schließen (`close`) — nach einer Ausführung übernimmt das `executeTransfer`.
    const [voidLink] = useVoidMolliePaymentLinkForBookingMutation();
    const paymentVoidedRef = useRef(false);

    const waitlist = (targetCourse?.freePlaces ?? 0) <= 0;
    const targetBookingType = waitlist ? BookingType.Waitlist : BookingType.Booking;

    /**
     * `transferDate` der ersten Vorschau geht in jede weitere Vorschau und in die Ausführung
     * zurück: Stichtag für „anteilig" und „besuchte Lektionen" — ohne ihn rechnete der Server
     * mit einem neuen „jetzt" und der Drift-Guard bräche ab, sobald dazwischen eine Lektion
     * begonnen hat (#866).
     */
    const buildPlanInput = (kind: TransferPriceKind): TransferPlanInput => ({
        sourceBookingId: bookingId,
        targetCourseId: targetCourse?.id ?? selectedCourse?.id ?? "",
        targetBookingType,
        priceOption: {
            kind,
            transferDate: basePlan?.transferDate,
            ...(kind === TransferPriceKind.Manual ? {amount: decisions.manualPrice.amount, reason: decisions.manualPrice.reason.trim()} : {}),
        },
        ...(decisions.itemDecisions.length > 0 ? {itemDecisions: decisions.itemDecisions} : {}),
        // Stichtag-Regel nur senden, wenn gewählt; ohne Feld gilt serverseitig „Geld folgt der Buchung".
        ...(decisions.oldBookingOption === OLD_BOOKING_CUTOFF ? {oldBookingOption: TransferOldBookingOption.CutoffRule} : {}),
        // #665: dieselbe Wahl in Vorschau und Ausführung — das Bestätigungstoken enthält sie.
        // Guthaben ist der Server-Default und wird nicht gesendet; `reason` nur bei manueller
        // Erstattung, ein Grund aus einer zurückgenommenen Wahl geht nicht mit.
        ...(decisions.surplusChoice.kind !== SURPLUS_CREDIT ? {surplusHandling: {kind: decisions.surplusChoice.kind, ...(decisions.surplusChoice.kind === TransferSurplusHandling.ManualRefund ? {reason: decisions.surplusChoice.reason.trim()} : {})}} : {}),
        // Verrechnetes Guthaben von A: IMMER explizit senden — ohne Feld gilt serverseitig das alte
        // Verhalten (bleibt Guthaben), damit bereits geladene ältere Bundles keine stille Verrechnung auslösen.
        creditedBalanceHandling: decisions.creditChoice,
    });
    const planInput = decisions.priceOption ? buildPlanInput(decisions.priceOption) : undefined;
    const planKey = JSON.stringify(planInput);
    const planStale = !plan || loadedKey !== planKey;
    const communication = resolveCommunication(plan, waitlist, communicationOverrides);

    const loadPlan = async (input: TransferPlanInput): Promise<Plan> => {
        const previewResult = await loadTransferPlanPreview({variables: {input}});

        // `useLazyQuery` liefert Fehler je nach Apollo-Pfad als `error` ODER als Rejection.
        if (previewResult.error) throw previewResult.error;
        const preview = previewResult.data?.transferPlanPreview;

        if (!preview) throw new Error(tr.errors.previewFailed());

        return preview;
    };

    /**
     * Gemeinsamer Fehlerweg: aktiver Mollie-Zahlungslink → Rückfrage, Link entwerten und
     * danach `afterVoid` (Default: dieselbe Aktion). Nach dem Void rechnet der Server
     * anders — alte Zahlen dürfen nicht weiterverwendet werden; die Ausführung lädt deshalb
     * nur die Vorschau neu und wartet auf einen erneuten Klick. Alles andere landet als
     * Text im Schritt.
     */
    const runGuarded = async (action: () => Promise<void>, afterVoid?: () => Promise<void>): Promise<void> => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setErrorText(undefined);
        try {
            try {
                await action();
            } catch (error) {
                if (!isMountedRef.current) return;
                if (extractGraphQlErrorCode(error) !== "MOLLIE_ACTIVE_PAYMENT_LINK") throw error;
                const trLink = i18n.containers.bookings.TransferMolliePaymentLink.single;
                const confirmed = await new Promise<boolean>((resolve) => {
                    Modal.confirm({width: 600, icon: null, title: trLink.modalTitle(), content: trLink.modalContent(), okText: trLink.okText(), onOk: () => resolve(true), onCancel: () => resolve(false)});
                });

                if (!confirmed || !isMountedRef.current) return;
                const hideLoading = message.loading(trLink.voidingMessage(), 0);

                try {
                    // Vor dem Aufruf: auch ein fehlgeschlagener Versuch kann Links schon entwertet haben (#1034).
                    paymentVoidedRef.current = true;
                    await voidLink({variables: {bookingId}});
                } finally {
                    hideLoading();
                }
                if (!isMountedRef.current) return;
                await (afterVoid ?? action)();
            }
        } catch (error) {
            if (isMountedRef.current) {
                setErrorText(error instanceof Error ? error.message : String(error));
            }
        } finally {
            busyRef.current = false;
            if (isMountedRef.current) setBusy(false);
        }
    };

    /**
     * Basis-Vorschauen zum Zielkurs, beide mit dem Vorschlag „Guthaben auf B verrechnen": die Seite
     * zeigt sie, bis die Vorschau zu den Entscheidungen da ist — ohne das Feld rechnete der Server
     * „bleibt Guthaben" (Legacy-Default für ältere Bundles) und die Raten sprängen beim ersten Laden.
     * Die zweite Vorschau MIT Rabatt, damit die Preis-Option beide Preise exakt so zeigt, wie der
     * Server sie rechnet (inkl. Klemmung auf den Kurspreis).
     */
    const loadBasePlans = async (target: TableType): Promise<{base: Plan; withDiscount?: Plan}> => {
        const type = (target.freePlaces ?? 0) > 0 ? BookingType.Booking : BookingType.Waitlist;
        const base = await loadPlan({sourceBookingId: bookingId, targetCourseId: target.id, targetBookingType: type, priceOption: {kind: TransferPriceKind.CoursePrice}, creditedBalanceHandling: CREDIT_REAPPLY});
        const withDiscount = base.sourceDiscount > 0
            ? await loadPlan({sourceBookingId: bookingId, targetCourseId: target.id, targetBookingType: type, priceOption: {kind: TransferPriceKind.KeepSourceDiscount, transferDate: base.transferDate}, creditedBalanceHandling: CREDIT_REAPPLY})
            : undefined;

        return {base, withDiscount};
    };

    // ── Schrittwechsel ────────────────────────────────────────────────────────────────
    const leaveTargetStep = () => runGuarded(async () => {
        const selectedCourseId = selectedCourse?.id;

        if (!selectedCourseId) return;
        // Die Auswahl kann seit dem Öffnen veraltet sein: freie Plätze frisch laden und den
        // Zieltyp (Buchung/Warteliste) daraus ableiten.
        const refreshed = await refetchCourses();
        const target = refreshed.data.coursesWithPagination.items.find((course) => course.id === selectedCourseId) as TableType | undefined;

        if (!isMountedRef.current) return;
        if (!target) {
            setSelectedCourse(undefined);
            throw new Error(tr.errors.targetUnavailable());
        }
        const {base, withDiscount} = await loadBasePlans(target);

        if (!isMountedRef.current) return;
        setTargetCourse(target);
        setBasePlan(base);
        setDiscountPlan(withDiscount);
        setCreditPlans(undefined);
        setPlan(undefined);
        setLoadedKey(undefined);
        attemptedKeyRef.current = undefined;
        // Neuer Zielkurs = neuer Plan: Entscheidungen auf die Tenant-Standardwerte zurücksetzen.
        // Bei Rabatt in A keine stille Vorauswahl (#852) — außer der Tenant will ausdrücklich
        // „übernehmen". Ohne Rabatt ist Kurspreis die einzige Option.
        const defaultKeep = defaults?.priceOption === TransferPriceKind.KeepSourceDiscount;
        // Läuft der Zielkurs schon und hat noch Lektionen, ist „anteilig" der Vorschlag (#1557).
        const suggestedPrice = proRataSuggested(base.priceOptions) ? TransferPriceKind.ProRataRemainingLessons : TransferPriceKind.CoursePrice;

        setDecisions({
            priceOption: base.sourceDiscount > 0 ? (defaultKeep ? TransferPriceKind.KeepSourceDiscount : undefined) : suggestedPrice,
            manualPrice: {reason: ""},
            // Stichtag-Regel, wo anwendbar; sonst (und bei Mandanten-Default `MoneyFollowsBooking`) „Geld folgt der Buchung".
            oldBookingOption: defaultOldBookingOption(base, defaults?.oldBookingOption),
            itemDecisions: (base.itemReview ?? []).filter((item) => item.open > 0).map((item) => ({sourceRateId: item.sourceRateId, carry: true})),
            surplusChoice: {kind: SURPLUS_CREDIT, reason: ""},
            creditChoice: CREDIT_REAPPLY,
        });
        setCommunicationOverrides({});
        setStep(STEP_SUMMARY);
    });

    /**
     * Vorschau zu den aktuellen Entscheidungen laden, sobald sie gültig sind und sich der
     * Plan-Input geändert hat. Läuft immer nur einmal gleichzeitig (`busy`); ändert sich
     * währenddessen etwas, holt der nächste Durchlauf den letzten Stand nach.
     */
    useEffect(() => {
        if (step !== STEP_SUMMARY || busy || !planInput || !decisionsValid(decisions) || attemptedKeyRef.current === planKey) return;
        attemptedKeyRef.current = planKey;
        runGuarded(async () => {
            const next = await loadPlan(planInput);
            // Gegenstück mit der anderen Guthaben-Wahl, damit beide Guthaben-Karten die Folgen
            // zum aktuellen Input nennen (Review #1000: sonst veralten sie nach einer Preiswahl).
            // Reine Anzeige: schlägt es fehl, bleiben die generischen Beschreibungen — die
            // ausführbare Vorschau `next` darf davon nicht blockiert werden.
            const other = planInput.creditedBalanceHandling === CREDIT_REAPPLY ? CREDIT_KEEP : CREDIT_REAPPLY;
            const alt = basePlan && basePlan.sourceCreditedBalance > 0 && !waitlist ? await loadPlan({...planInput, creditedBalanceHandling: other}).catch(() => undefined) : undefined;

            if (!isMountedRef.current) return;
            setPlan(next);
            setCreditPlans(alt ? (other === CREDIT_KEEP ? {reapply: next, keep: alt} : {reapply: alt, keep: next}) : undefined);
            setLoadedKey(planKey);
        });
    }, [step, busy, planKey, decisions]);

    /** Nach dem Entwerten eines Zahlungslinks rechnet der Server anders: Vorschau verwerfen, der Effekt lädt sie neu. */
    const invalidatePlan = async () => {
        if (!isMountedRef.current) return;
        setPlan(undefined);
        setLoadedKey(undefined);
        attemptedKeyRef.current = undefined;
    };

    /**
     * Blocker „aktiver Zahlungslink" aus der Vorschau: nach Rückfrage entwerten, dann Basis- und
     * Entscheidungs-Vorschau neu laden — die Entscheidungen bleiben. Umgebucht wird erst mit dem
     * nächsten Klick auf „Ausführen". (Der Fehlerweg in `runGuarded` fängt nur noch den Fall ab,
     * dass zwischen Vorschau und Ausführung ein Link entstanden ist.)
     */
    const voidPaymentLink = () => {
        const trLink = tr.voidLink;

        Modal.confirm({
            width: 600,
            icon: null,
            title: trLink.confirmTitle(),
            content: trLink.confirmContent({booking: sourceBooking ? `${sourceBooking.bookingNumberPrefix}-${sourceBooking.bookingNumber}` : ""}),
            okText: trLink.button(),
            okButtonProps: {danger: true},
            onOk: async () => runGuarded(async () => {
                const hideLoading = message.loading(trLink.voiding(), 0);

                try {
                    // Vor dem Aufruf: auch ein fehlgeschlagener Versuch kann Links schon entwertet haben (#1034).
                    paymentVoidedRef.current = true;
                    await voidLink({variables: {bookingId}});
                } finally {
                    hideLoading();
                }
                if (!isMountedRef.current || !targetCourse) return;
                const {base, withDiscount} = await loadBasePlans(targetCourse);

                if (!isMountedRef.current) return;
                setBasePlan(base);
                setDiscountPlan(withDiscount);
                setCreditPlans(undefined);
                await invalidatePlan();
                // Maßgeblich ist die neue Vorschau: meldet sie weiter einen offenen Link, hat das Entwerten
                // nicht gegriffen. Als Meldung, nicht als `errorText` — das Neuladen der Vorschau leert ihn.
                if (base.hasActivePaymentLink) {
                    message.error(i18n.containers.bookings.TransferMolliePaymentLink.single.errorMessage());
                } else {
                    message.success(trLink.done());
                }
            }),
        });
    };

    const execute = () => runGuarded(async () => {
        try {
            await runExecution();
        } catch (error) {
            const detail = outcomeUnknownDetail(error);

            if (detail === undefined) throw error;
            if (isMountedRef.current) setOutcomeUnknown(detail);
        }
    }, invalidatePlan);

    /** Ausführung mit dem Token der aktuellen Vorschau. */
    const runExecution = async () => {
        if (!plan || !planInput || planStale || !targetCourse || plan.blockers.length > 0) return;
        const executionResult = await executeTransfer({
            variables: {
                input: {
                    plan: planInput,
                    confirmationToken: plan.confirmationToken,
                    expectedPriceB: plan.priceB,
                    expectedDiscountB: plan.discountB,
                    expectedAppliedToB: plan.appliedToB,
                    expectedSurplus: plan.surplus,
                    expectedOldBookingTreatment: plan.oldBooking.treatment,
                    expectedCarriedOverItems: plan.carriedOverItems.map((item) => ({type: item.type, amount: item.amount, title: item.title ?? null})),
                    deleteNotes,
                    // Nachrücken im Quellkurs ist eine offene Produktentscheidung (#859) — wie
                    // im Legacy-Dialog wird niemand automatisch nachgerückt.
                    sourceDeleteAction: {type: BookingDeleteActionType.DoNothing},
                    communication,
                },
            },
        });
        const executed = executionResult.data?.executeTransfer;

        if (!executed) throw new Error(tr.errors.executionFailed());
        message.success(`${attendeeFullName ?? ""} ${executed.booking.bookingType === BookingType.Waitlist ? tr.result.movedToWaitlist() : tr.result.movedToCourse()} ${targetCourse.prefixedCourseNumber} ${tr.result.moved()}`);
        // Bewusst auch nach einem Unmount: das Ergebnis (inkl. warnings) hängt nicht am Dialog.
        showResult(executed);
        if (isMountedRef.current) setModalOpen(false);
    };

    const close = () => {
        if (paymentVoidedRef.current || outcomeUnknown !== undefined) {
            // Namen UND Dokumente: `include` filtert in beiden Fällen nur gemountete Abfragen (kein Einmal-Fetch).
            apolloClient.refetchQueries({include: transferRefetchQueries.map((entry) => (typeof entry === "string" ? entry : entry.query))}).catch(() => undefined);
        }
        setModalOpen(false);
    };

    // ── Footer ────────────────────────────────────────────────────────────────────────
    const executeDisabled = busy || outcomeUnknown !== undefined || planStale || !decisionsValid(decisions) || (plan?.blockers.length ?? 0) > 0 || Boolean(errorText);
    const footer = (
        <Space>
            <Button danger onClick={close}>{tr.buttons.cancel()}</Button>
            {step === STEP_TARGET && placeReservationButton && placeReservationButton(selectedCourse, busy)}
            {step === STEP_SUMMARY && <Button onClick={() => {
                // #1232: Die Meldung gehört zur Zusammenfassung; in Schritt 1 wäre sie irreführend.
                setErrorText(undefined);
                setStep(STEP_TARGET);
            }} disabled={busy}>{tr.buttons.back()}</Button>}
            {step === STEP_TARGET && <Button type="primary" loading={busy} disabled={busy || !selectedCourse} onClick={leaveTargetStep}>{tr.buttons.next()}</Button>}
            {step === STEP_SUMMARY && <Button type="primary" loading={busy} disabled={executeDisabled} onClick={execute}>{tr.buttons.execute()}</Button>}
        </Space>
    );

    // ── Inhalt ────────────────────────────────────────────────────────────────────────
    const sourceLabel = sourceBooking ? `${sourceBooking.bookingNumberPrefix}-${sourceBooking.bookingNumber}` : "";
    /** Eckdaten-Zeile einer Karte: „Montag 08:00–09:00 · Ort · 10 Lektionen ab 21. Sept. 2026" — nur die Teile, die vorliegen. */
    const courseMeta = (lessons: Array<{startDateTime?: string | null; endDateTime?: string | null}>, location?: string | null): string | undefined => {
        const sorted = lessons.filter((lesson) => Boolean(lesson.startDateTime)).sort((a, b) => String(a.startDateTime).localeCompare(String(b.startDateTime)));
        const first = sorted[0];

        if (!first) return location ?? undefined;
        const start = new Date(String(first.startDateTime));
        const time = `${formatDate(start, "EEEE HH:mm")}${first.endDateTime ? `–${formatDate(new Date(String(first.endDateTime)), "HH:mm")}` : ""}`;

        return [time, location, (sorted.length === 1 ? tr.summary.lessonFromOne({date: formatDate(start, "d. MMM yyyy")}) : tr.summary.lessonsFrom({count: String(sorted.length), date: formatDate(start, "d. MMM yyyy")}))].filter(Boolean).join(" · ");
    };
    const sourceLessons = sourceBooking?.courseLessons ?? [];
    const sourceCourseData = sourceLessons.find((lesson) => Boolean(lesson.course?.prefixedCourseNumber))?.course;
    const sourceCourse: CourseInfo = {
        number: sourceCourseData?.prefixedCourseNumber ?? undefined,
        typeName: sourceCourseData?.courseType?.name ?? undefined,
        typeColor: sourceCourseData?.courseType?.color ?? undefined,
        meta: courseMeta(sourceLessons, sourceLessons.find((lesson) => Boolean(lesson.location?.name))?.location?.name),
    };
    const targetCourseInfo: CourseInfo = {
        number: targetCourse?.prefixedCourseNumber ?? undefined,
        typeName: targetCourse?.courseType?.name ?? undefined,
        typeColor: targetCourse?.courseType?.color ?? undefined,
        meta: targetCourse ? courseMeta(targetCourse.lessons ?? [], targetCourse.location?.name) : undefined,
    };

    const renderStep = () => {
        if (step === STEP_SUMMARY && basePlan) {
            return <TransferSummary
                basePlan={basePlan}
                discountPlan={discountPlan}
                creditPlans={creditPlans}
                oldBookingDefault={defaults?.oldBookingOption}
                plan={plan}
                loading={busy}
                currency={currency}
                waitlist={waitlist}
                sourceLabel={sourceLabel}
                sourcePrice={sourceBooking?.grossPrice}
                sourceCourse={sourceCourse}
                targetCourse={targetCourseInfo}
                decisions={decisions}
                onDecisionsChange={(patch) => setDecisions({...decisions, ...patch})}
                communication={communication}
                onCommunicationChange={(patch) => setCommunicationOverrides({...communicationOverrides, ...patch})}
                deleteNotes={deleteNotes}
                onDeleteNotesChange={setDeleteNotes}
                canWaive={!isStaffOnlyAccess}
                onVoidPaymentLink={voidPaymentLink}
            />;
        }

        return <div>
            {isStaffOnlyAccess && <Alert style={{marginBottom: marginS}} type="info" showIcon message={i18n.containers.bookings.TransferEngineConfirm.staffOnlyHint()} />}
            <ActiveDunningAlert bookingId={bookingId} variant="transfer" />
            <CourseList
                tableData={tableData}
                options={options}
                setOptions={setOptions}
                refetch={refetchCourses}
                defaultPageSize={10}
                bookingCourse
                toolBarButtons={["columnSelector"]}
                hiddenColumns={["actions"]}
                defaultColumns={["prefixedCourseNumber", "courseType", "firstCourseLesson", "location", "freePlaces", "placeReservationsCount"]}
                // #1232: Nach „Zurück“ bleibt der Zielkurs gewählt; ohne Vorauswahl meldet die Liste beim
                // Einhängen eine leere Auswahl und „Weiter“ wäre gesperrt.
                selectedCourseId={selectedCourse?.id}
                handleCourseSelection={(selectedCourses: Array<TableType>) => {
                    setSelectedCourse(selectedCourses[0]);
                    onCourseSelected?.(selectedCourses[0]);
                }}
                tablesEnum={TablesEnum.CourseTransferAttendeeToCourse}
            />
            <div style={{marginTop: marginS}}>
                {selectedCourse ? <span>{tr.target.selected()}{": "}<b>{selectedCourse.prefixedCourseNumber}</b>{" — "}{(selectedCourse.freePlaces ?? 0) > 0 ? tr.target.asBooking() : tr.target.asWaitlist()}</span> : tr.target.none()}
            </div>
        </div>;
    };

    /**
     * Ergebnis als EIGENSTÄNDIGER Dialog (Modal.confirm), nicht im Wizard-Modal: die
     * Refetches nach `executeTransfer` entfernen die Zeile der alten Buchung aus der Liste,
     * und mit ihr wird diese Komponente abgebaut. Ein deklarativ gerendertes Ergebnis wäre
     * damit weg, inklusive `warnings` und Links (Browser-Befund 2026-09-11).
     */
    const showResult = (executed: ExecuteResult) => {
        const course = targetCourse ?? selectedCourse;
        const linkToCourse = course ? {pathname: URL_COURSES_DETAILS_ID.replace(":id", course.id), state: {returnTo: window.location.pathname}} : undefined;
        // Mit Rechnung B ist die neue Buchung abgerechnet: dann auf die Abrechnungsdetails,
        // nicht auf den Editor (#1233). Einzelbuchungs-Zielkurse (#1556): die neue Buchung trägt
        // alle Lektionen und gehört in den Einzelbuchungs-Rahmen, wie nach dem Legacy-Dialog.
        const settled = Boolean(executed.invoiceB);
        const lessonCourse = Boolean(course?.allowLessonBooking);
        const linkToNewBooking = (isStaffOnlyAccess
            ? (settled ? URL_INSTRUCTOR_INVOICE_SETTLEMENT_DETAILS : URL_INSTRUCTOR_BOOKING_EDIT_ID)
            : lessonCourse
                ? (settled ? URL_LESSON_BOOKINGS_SETTLED_DETAILS : URL_LESSON_BOOKING_EDIT_ID)
                : (settled ? URL_INVOICE_SETTLEMENT_DETAILS : URL_BOOKING_EDIT_ID)).replace(":id", executed.booking.id);
        const goTo = (link: string | {pathname: string; state: {returnTo: string}}) => {
            history.push(link);
            Modal.destroyAll();
        };
        const booker = sourceBooking?.attendee?.booker;

        Modal.confirm({
            icon: null,
            width: TRANSFER_WIZARD_WIDTH_COMPACT,
            okText: tr.buttons.done(),
            okButtonProps: {block: true, style: {margin: 0}},
            cancelButtonProps: {style: {display: "none"}},
            maskClosable: true,
            content: <ApolloProvider client={apolloClient}><TransferResult
                executed={executed}
                attendeeFullName={attendeeFullName}
                bookerName={booker ? `${booker.firstname} ${booker.lastname}` : undefined}
                sourceLabel={sourceLabel}
                sourceCourse={sourceCourse}
                targetCourse={{...targetCourseInfo, number: course?.prefixedCourseNumber ?? targetCourseInfo.number}}
                communication={communication}
                currency={currency}
                onGoToCourse={linkToCourse ? () => goTo(linkToCourse) : undefined}
                onGoToBooking={() => goTo(linkToNewBooking)}
            /></ApolloProvider>,
        });
    };

    return (
        <Modal
            title={<span>{tr.title()}{" — "}<span style={{fontWeight: 400}}>{attendeeFullName}</span></span>}
            open={modalOpen}
            onCancel={close}
            width={step === STEP_TARGET ? TRANSFER_WIZARD_WIDTH_FULL : TRANSFER_WIZARD_WIDTH_COMPACT}
            style={transferWizardModalStyle}
            footer={footer}
            destroyOnClose
        >
            <div>
                {outcomeUnknown !== undefined && <OutcomeUnknownAlert detail={outcomeUnknown} />}
                {errorText && <Alert type="error" showIcon closable onClose={() => setErrorText(undefined)} style={{marginBottom: 12}} message={errorText} />}
                {renderStep()}
            </div>
        </Modal>
    );
};

export default memo(TransferWizard);
